import PropTypes from 'prop-types';
import { useState, useEffect, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// material-ui
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';

// project import
import NavItem, { get3DIcon } from './NavItem';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST GROUP ||============================== //

const NavGroup = memo(function NavGroup({ item }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isSelected = item.children?.some(child => child.url && pathname.startsWith(child.url));
  const [open, setOpen] = useState(isSelected || false);

  // Auto-open if a child route becomes active
  useEffect(() => {
    if (isSelected) {
      setOpen(true);
    }
  }, [isSelected]);

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'collapse':
        return (
          <Typography key={menuItem.id} variant="caption" color="error" sx={{ p: 2.5 }}>
            collapse - only available in paid version
          </Typography>
        );
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;
      default:
        return null;
    }
  });

  const Icon = item.icon || get3DIcon(item.id);
  const groupIcon = <Icon style={{ fontSize: '1.25rem' }} />;

  const handleGroupClick = () => {
    if (item.id === 'dental-dashboard-group' && item.children?.length === 1 && item.children[0]?.url) {
      navigate(item.children[0].url);
      return;
    }
    if (!drawerOpen) {
      handlerDrawerOpen(true);
      setOpen(true);
    } else {
      setOpen((prev) => !prev);
    }

    if (item.url && item.url !== '#' && item.url !== '/') {
      navigate(item.url);
    } else if ((item.id === 'enterprise_erp' || item.code === 'enterprise_erp') && item.children?.[0]?.url) {
      navigate(item.children[0].url);
    }
  };

  // isSelected moved up

  return (
    <List className="nav-group-list" sx={{ mb: 0.5, py: 0, px: 0.5 }}>
      {/* Clickable category header */}
      {item.title && (
        <ListItemButton
          className="nav-group-header"
          onClick={handleGroupClick}
          sx={(theme) => ({
            px: 1.5,
            py: 1,
            mb: 0.5,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: isSelected ? 'rgba(22, 119, 255, 0.06)' : 'transparent',
            boxShadow: 'none !important',
            border: 'none !important',
            outline: 'none !important',
            '&:hover': { bgcolor: isSelected ? 'rgba(22, 119, 255, 0.1)' : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#F8FAFC' }
          })}
          title={!drawerOpen ? item.title : ''}
        >
          {/* ICON (Always visible clean icon) */}
          <ListItemIcon
            className="nav-group-icon"
            sx={(theme) => ({
              display: 'flex',
              minWidth: 28,
              width: 28,
              height: 28,
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.main,
              bgcolor: 'transparent',
              mr: 1.25,
              boxShadow: 'none',
              border: 'none',
              outline: 'none'
            })}
          >
            {groupIcon}
          </ListItemIcon>

          {/* TITLE & CHEVRON (Visible only when open) */}
          <Box
            className="nav-group-title-container"
            sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography
              variant="body1"
              sx={(theme) => ({ 
                color: theme.palette.text.primary, 
                fontWeight: 600, 
                fontSize: '0.875rem', 
                userSelect: 'none',
                whiteSpace: 'nowrap'
              })}
            >
              {item.title}
            </Typography>

            {item.children && item.id !== 'dental-dashboard-group' && (
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'transform 0.25s ease',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: theme.palette.text.secondary,
                  fontSize: 12
                })}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Box>
            )}
          </Box>
        </ListItemButton>
      )}

      {/* Collapsible items: Display none when drawer is closed to prevent lag from unmounting */}
      <Box className="nav-group-collapse-container" sx={{ display: 'block' }}>
        <Collapse in={open} timeout={180}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {navCollapse}
          </Box>
        </Collapse>
      </Box>
    </List>
  );
});

export default NavGroup;

NavGroup.propTypes = { item: PropTypes.object };

